// src/store/event-portal-store.ts
import type { EFGEvent } from '@/types';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

// ============================================================================
// Types
// ============================================================================

export interface EventPortalState {
  // Persisted state
  event: EFGEvent | null;
  eventToken: string;

  // Hydration state (tracks if store has loaded from localStorage)
  isHydrated: boolean;
}

interface EventPortalActions {
  setEvent: (event: EFGEvent | null) => void;
  setEventToken: (token: string) => void;
  doLogout: () => void;
  setHydrated: () => void;

  // Computed getters
  isAuthenticated: () => boolean;
}

type EventPortalStore = EventPortalState & EventPortalActions;

// ============================================================================
// Initial State
// ============================================================================

const initialState: EventPortalState = {
  event: null,
  eventToken: '',
  isHydrated: false,
};

// ============================================================================
// Store Implementation
// ============================================================================

export const useEventPortalStore = create<EventPortalStore>()(
  persist(
    (set, get) => ({
      // State
      ...initialState,

      // Actions
      setEvent: (event: EFGEvent | null) => set({ event }),

      setEventToken: (token: string) => set({ eventToken: token }),

      doLogout: () =>
        set({
          eventToken: '',
          event: null,
        }),

      setHydrated: () => set({ isHydrated: true }),

      // Computed
      isAuthenticated: () => {
        const state = get();
        return Boolean(state.eventToken && state.event);
      },
    }),
    {
      name: 'event-portal-store', // localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist these fields
      partialize: (state) => ({
        event: state.event,
        eventToken: state.eventToken,
      }),
      // Set hydrated flag when store is rehydrated from localStorage
      onRehydrateStorage: () => (state) => {
        state?.setHydrated();
      },
    }
  )
);

// ============================================================================
// Selectors (for optimized re-renders)
// ============================================================================

export const selectEvent = (state: EventPortalStore) => state.event;
export const selectEventToken = (state: EventPortalStore) => state.eventToken;
export const selectEventPortalIsAuthenticated = (state: EventPortalStore) =>
  Boolean(state.eventToken && state.event);
export const selectEventPortalIsHydrated = (state: EventPortalStore) => state.isHydrated;
